/**
 * Edición manual de una ficha (§7, §10.6).
 *
 * Solo se envían los campos que de verdad cambiaron: el backend marca
 * `campos_editados` con lo que llega en el PATCH, y mandar el formulario
 * completo marcaría como «editado a mano» todo lo que el usuario ni tocó, que
 * es justo la distinción que la interfaz tiene que preservar.
 */

import { useMemo, useState } from 'react'

import { useEditarStakeholder, useEnums } from '../../api/hooks'
import type { Stakeholder } from '../../api/tipos'
import { mensajeDeError, useAvisos } from '../../ui/avisos-contexto'
import { Dialogo } from '../../ui/Dialogo'
import { AreaTexto, Boton, Campo, Entrada, Seleccion } from '../../ui/primitivos'

/** Campos de texto simple, con su etiqueta y si van en varias líneas. */
const CAMPOS_TEXTO = [
  { campo: 'nombre', etiqueta: 'Nombre', filas: 0 },
  { campo: 'sigla', etiqueta: 'Sigla', filas: 0 },
  { campo: 'categoria', etiqueta: 'Categoría', filas: 0 },
  { campo: 'subcategoria', etiqueta: 'Subcategoría', filas: 0 },
  { campo: 'descripcion', etiqueta: 'Descripción', filas: 4 },
  { campo: 'relacion_con_entidad', etiqueta: 'Por qué es stakeholder', filas: 3 },
] as const

const CAMPOS_LISTA = [
  { campo: 'intereses', etiqueta: 'Intereses' },
  { campo: 'riesgos_o_tensiones', etiqueta: 'Riesgos o tensiones' },
  { campo: 'mecanismos_de_relacionamiento', etiqueta: 'Mecanismos de relacionamiento' },
  { campo: 'territorios', etiqueta: 'Territorios' },
] as const

type Borrador = Record<string, unknown>

function desdeFicha(ficha: Stakeholder): Borrador {
  const borrador: Borrador = {}
  for (const { campo } of CAMPOS_TEXTO) borrador[campo] = ficha[campo] ?? ''
  for (const { campo } of CAMPOS_LISTA) {
    // Las listas se editan como una línea por elemento: es lo que espera
    // alguien que corrige a mano, no un JSON.
    borrador[campo] = (ficha[campo] ?? []).join('\n')
  }
  borrador.tipo = ficha.tipo
  borrador.nivel_influencia = ficha.nivel_influencia
  borrador.nivel_impacto_recibido = ficha.nivel_impacto_recibido
  borrador.postura = ficha.postura
  borrador.confianza = ficha.confianza === null ? '' : String(Math.round(ficha.confianza * 100))
  return borrador
}

/** Solo lo que cambió, ya convertido al formato que espera la API. */
function cambios(ficha: Stakeholder, borrador: Borrador): Partial<Stakeholder> {
  const salida: Record<string, unknown> = {}

  for (const { campo } of CAMPOS_TEXTO) {
    const nuevo = String(borrador[campo] ?? '').trim()
    if (nuevo !== (ficha[campo] ?? '')) salida[campo] = nuevo
  }

  for (const { campo } of CAMPOS_LISTA) {
    const nuevo = String(borrador[campo] ?? '')
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    const previo = ficha[campo] ?? []
    if (nuevo.length !== previo.length || nuevo.some((v, i) => v !== previo[i])) {
      salida[campo] = nuevo
    }
  }

  for (const campo of [
    'tipo',
    'nivel_influencia',
    'nivel_impacto_recibido',
    'postura',
  ] as const) {
    if (borrador[campo] !== ficha[campo]) salida[campo] = borrador[campo]
  }

  const texto = String(borrador.confianza ?? '').trim()
  const confianza = texto === '' ? null : Math.max(0, Math.min(100, Number(texto))) / 100
  if (texto !== '' && Number.isNaN(Number(texto))) {
    // Un valor no numérico se ignora en vez de romper el envío.
  } else if (confianza !== ficha.confianza) {
    salida.confianza = confianza
  }

  return salida as Partial<Stakeholder>
}

export function EditorFicha({
  ficha,
  abierto,
  onCerrar,
}: {
  ficha: Stakeholder
  abierto: boolean
  onCerrar: () => void
}) {
  const avisos = useAvisos()
  const enums = useEnums()
  const editar = useEditarStakeholder(ficha.entity)
  const [borrador, setBorrador] = useState<Borrador>(() => desdeFicha(ficha))

  const pendientes = useMemo(() => cambios(ficha, borrador), [ficha, borrador])
  const nPendientes = Object.keys(pendientes).length

  function guardar() {
    if (nPendientes === 0) {
      onCerrar()
      return
    }
    editar.mutate(
      { id: ficha.id, cambios: pendientes },
      {
        onSuccess: () => {
          avisos.exito(`«${ficha.nombre}» actualizado`, {
            detalle: `${nPendientes} campo${nPendientes === 1 ? '' : 's'} marcado${nPendientes === 1 ? '' : 's'} como edición manual.`,
          })
          onCerrar()
        },
        onError: (error) =>
          avisos.error('No se pudo guardar la edición', mensajeDeError(error)),
      },
    )
  }

  const puesto = (campo: string) => campo in pendientes

  return (
    <Dialogo
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Editar ficha"
      descripcion="Los campos que cambies quedan marcados como edición manual y ninguna corrida posterior los sobrescribe."
      ancho="lg"
      pie={
        <>
          <span className="mr-auto self-center text-[12px] text-[var(--color-texto-tenue)]">
            {nPendientes === 0
              ? 'Sin cambios'
              : `${nPendientes} campo${nPendientes === 1 ? '' : 's'} por guardar`}
          </span>
          <Boton variante="secundario" onClick={onCerrar} disabled={editar.isPending}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            onClick={guardar}
            cargando={editar.isPending}
            disabled={nPendientes === 0}
          >
            Guardar
          </Boton>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {CAMPOS_TEXTO.filter((c) => c.filas === 0).map(({ campo, etiqueta }) => (
            <Campo key={campo} etiqueta={etiqueta + (puesto(campo) ? ' •' : '')}>
              <Entrada
                value={String(borrador[campo] ?? '')}
                onChange={(e) => setBorrador({ ...borrador, [campo]: e.target.value })}
              />
            </Campo>
          ))}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta={'Tipo' + (puesto('tipo') ? ' •' : '')}>
            <Seleccion
              className="w-full"
              value={String(borrador.tipo)}
              onChange={(e) => setBorrador({ ...borrador, tipo: e.target.value })}
            >
              {enums.data?.tipo.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </Seleccion>
          </Campo>
          <Campo etiqueta={'Postura' + (puesto('postura') ? ' •' : '')}>
            <Seleccion
              className="w-full"
              value={String(borrador.postura)}
              onChange={(e) => setBorrador({ ...borrador, postura: e.target.value })}
            >
              {enums.data?.postura.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </Seleccion>
          </Campo>
          <Campo etiqueta={'Influencia' + (puesto('nivel_influencia') ? ' •' : '')}>
            <Seleccion
              className="w-full"
              value={String(borrador.nivel_influencia)}
              onChange={(e) => setBorrador({ ...borrador, nivel_influencia: e.target.value })}
            >
              {enums.data?.nivel.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </Seleccion>
          </Campo>
          <Campo etiqueta={'Impacto recibido' + (puesto('nivel_impacto_recibido') ? ' •' : '')}>
            <Seleccion
              className="w-full"
              value={String(borrador.nivel_impacto_recibido)}
              onChange={(e) =>
                setBorrador({ ...borrador, nivel_impacto_recibido: e.target.value })
              }
            >
              {enums.data?.nivel.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </Seleccion>
          </Campo>
        </div>

        <Campo
          etiqueta={'Confianza' + (puesto('confianza') ? ' •' : '')}
          ayuda="Porcentaje de 0 a 100. Vacío para dejarla sin definir."
        >
          <Entrada
            type="number"
            min={0}
            max={100}
            value={String(borrador.confianza ?? '')}
            onChange={(e) => setBorrador({ ...borrador, confianza: e.target.value })}
          />
        </Campo>

        {CAMPOS_TEXTO.filter((c) => c.filas > 0).map(({ campo, etiqueta, filas }) => (
          <Campo key={campo} etiqueta={etiqueta + (puesto(campo) ? ' •' : '')}>
            <AreaTexto
              rows={filas}
              value={String(borrador[campo] ?? '')}
              onChange={(e) => setBorrador({ ...borrador, [campo]: e.target.value })}
            />
          </Campo>
        ))}

        {CAMPOS_LISTA.map(({ campo, etiqueta }) => (
          <Campo
            key={campo}
            etiqueta={etiqueta + (puesto(campo) ? ' •' : '')}
            ayuda="Un elemento por línea."
          >
            <AreaTexto
              rows={3}
              value={String(borrador[campo] ?? '')}
              onChange={(e) => setBorrador({ ...borrador, [campo]: e.target.value })}
            />
          </Campo>
        ))}
      </div>
    </Dialogo>
  )
}
